import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  UserAttributes,
  UserContextResponse,
  Permission,
  hasPermission as checkPermission,
  canViewDLP,
} from '../types/auth';

interface AuthContextType {
  // Auth state
  isAuthenticated: boolean;
  accessToken: string | null;
  authReady: boolean;
  isLoading: boolean;

  // Firewall API base URL (for components that need to call the backend directly)
  apiUrl: string;

  // User context
  userAttributes: UserAttributes | null;

  // Permission checking
  hasPermission: (permission: Permission) => boolean;
  can: (action: string, resource?: string) => boolean;
  canViewDLPData: () => boolean;

  // Callbacks
  setUserAttributes: (attrs: UserAttributes | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthContextProviderProps {
  children: ReactNode;
  isAuthenticated: boolean;
  accessToken: string | null;
  authReady: boolean;
  apiUrl: string;
}

export const AuthContextProvider: React.FC<AuthContextProviderProps> = ({
  children,
  isAuthenticated,
  accessToken,
  authReady,
  apiUrl,
}) => {
  const [userAttributes, setUserAttributes] = useState<UserAttributes | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load user context: seed from cache immediately for a fast first paint, then
  // refresh from the backend. If the backend is unreachable, keep the cached
  // context rather than clearing it (avoids losing the role on a transient blip).
  useEffect(() => {
    // Gate on auth readiness only. The token is NOT always in React state —
    // for local/mock (POC) logins it lives in localStorage and is attached by the
    // global fetch interceptor. Requiring `accessToken` here would skip context
    // loading for those sessions.
    if (!isAuthenticated || !authReady) {
      setUserAttributes(null);
      return;
    }

    // Seed from cache synchronously so role-based UI renders without a flash.
    const cached = localStorage.getItem('ztai-user-context');
    if (cached) {
      try {
        setUserAttributes(JSON.parse(cached));
      } catch (e) {
        /* ignore corrupt cache */
      }
    }

    let cancelled = false;
    const fetchUserContext = async () => {
      setIsLoading(true);
      try {
        // Authorization header is added by the global fetch interceptor (SAML or
        // Keycloak). Pass it explicitly too when we have it in React state.
        const headers: Record<string, string> = {};
        if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
        const response = await fetch(`${apiUrl}/api/admin/user/context`, { headers });

        if (cancelled) return;

        if (response.ok) {
          const data: UserContextResponse = await response.json();
          const attrs: UserAttributes = {
            userId: data.userId,
            role: data.role,
            team: data.team,
            dataClassification: data.dataClassification,
            canApproveActions: data.canApproveActions,
            resourceScope: data.resourceScope,
            profile: data.profile,
            permissions: data.permissions,
          };
          setUserAttributes(attrs);
          // Cache the full context so a reload / offline blip keeps the role.
          localStorage.setItem('ztai-user-context', JSON.stringify(attrs));
        } else {
          // Auth-level failure (401/403): the identity is invalid — clear context.
          // Other statuses: keep whatever we seeded from cache.
          if (response.status === 401 || response.status === 403) {
            setUserAttributes(null);
            localStorage.removeItem('ztai-user-context');
          }
          console.warn('Failed to fetch user context:', response.statusText);
        }
      } catch (error) {
        // Network error — keep the cached context (best-effort offline support).
        console.warn('Error fetching user context (using cache if available):', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchUserContext();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, authReady, accessToken, apiUrl]);

  const hasPermissionFn = (permission: Permission): boolean => {
    if (!userAttributes) {
      return false;
    }
    return checkPermission(userAttributes, permission);
  };

  const canFn = (action: string, resource?: string): boolean => {
    // Generic action check; can be extended for resource-level ABAC later
    const permissionMap: Record<string, Permission> = {
      'view:dashboard': 'view:dashboard',
      'view:policies': 'view:policies',
      'create:policy': 'create:policy',
      'update:policy': 'update:policy',
      'delete:policy': 'delete:policy',
      'view:agents': 'view:agents',
      'deploy:agent': 'deploy:agent',
      'approve:agent-deploy': 'approve:agent-deploy',
      'view:audit-logs': 'view:audit-logs',
      'view:violations': 'view:violations',
      'view:dlp-data': 'view:dlp-data',
      'export:dlp-data': 'export:dlp-data',
      'manage:users': 'manage:users',
      'manage:settings': 'manage:settings',
      'view:mcp-servers': 'view:mcp-servers',
      'configure:mcp-servers': 'configure:mcp-servers',
    };

    const permission = permissionMap[action];
    if (!permission) {
      console.warn(`Unknown action: ${action}`);
      return false;
    }

    return hasPermissionFn(permission);
  };

  const canViewDLPDataFn = (): boolean => {
    if (!userAttributes) {
      return false;
    }
    return canViewDLP(userAttributes);
  };

  const value: AuthContextType = {
    isAuthenticated,
    accessToken,
    authReady,
    isLoading,
    apiUrl,
    userAttributes,
    hasPermission: hasPermissionFn,
    can: canFn,
    canViewDLPData: canViewDLPDataFn,
    setUserAttributes,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthContextProvider');
  }
  return context;
};
